import { Link } from 'react-router-dom'
import { getMyOrders } from '../api/client'
import type { Order } from '../api/types'
import { useAuth } from '../auth'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Standmarke } from '../components/OrderTracker'
import { Fehlerhinweis, Ladehinweis } from '../components/States'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { mitElement, useDatumFormat, useSprache } from '../i18n'
import { usePriceFormat, useShopConfig } from '../shopConfig'

/**
 * Meine Bestellungen.
 *
 * Das ist der eine Grund, aus dem sich jemand hier anmeldet: Ohne Konto
 * braucht jede Nachschau Bestellnummer und E-Mail-Adresse, und die Nummer hat
 * man selten zur Hand. Angemeldet steht die Liste einfach da.
 *
 * Karten statt Zeilen, und zusammengeklappt: Wer zwanzig Bestellungen hat,
 * sucht eine bestimmte — und sucht sie am Stand, am Datum und am Betrag, nicht
 * in zwanzig aufgeklappten Positionslisten. Die drei Angaben stehen deshalb
 * groß auf der Karte, die Einzelheiten eine Klickbewegung entfernt.
 */
export function MyOrdersPage() {
  const { t, tz } = useSprache()
  const { shopName, primaryHost } = useShopConfig()
  const { ich, laedt: meldungLaeuft } = useAuth()
  const preis = usePriceFormat()
  const datum = useDatumFormat()

  useSeo({
    title: t('meine.seoTitel', { laden: shopName }),
    description: t('meine.seoText'),
    canonicalHost: primaryHost,
  })

  // Erst fragen, wenn feststeht, dass jemand angemeldet ist: Der Aufruf
  // antwortet sonst mit 401, und eine Fehlermeldung ist die falsche Antwort auf
  // „noch nicht geladen".
  const angemeldet = ich.signedIn
  const { data, error, loading } = useResource(
    (signal) => (angemeldet ? getMyOrders(signal) : Promise.resolve<Order[]>([])),
    [angemeldet],
  )

  const kopf = (
    <>
      <Breadcrumbs
        pfade={[{ name: t('nav.startseite'), pfad: '/' }, { name: t('meine.titel') }]}
      />
      <h1>{t('meine.titel')}</h1>
    </>
  )

  if (meldungLaeuft) {
    return (
      <>
        {kopf}
        <Ladehinweis />
      </>
    )
  }

  if (!angemeldet) {
    return (
      <>
        {kopf}
        <p className="hinweis">
          {t('meine.nurAngemeldet')}{' '}
          <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>.
        </p>
      </>
    )
  }

  const bestellungen = data ?? []

  return (
    <>
      {kopf}
      <p className="beschreibung">
        {mitElement(
          t('meine.text', { konto: ich.email || t('meine.diesesKonto') }),
          '{verweis}',
          <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>,
        )}
      </p>

      {loading && <Ladehinweis text={t('meine.laedt')} />}
      {!loading && error != null && <Fehlerhinweis fehler={error} />}

      {!loading && error == null && bestellungen.length === 0 && (
        <p className="hinweis">
          {t('meine.leer')} {t('korb.stoebern')} <Link to="/suche">{t('korb.sortiment')}</Link>.
        </p>
      )}

      <ul className="bestellkarten">
        {bestellungen.map((bestellung) => {
          const stueck = bestellung.lines.reduce((summe, zeile) => summe + zeile.quantity, 0)

          return (
            <li className="bestellkarte" key={bestellung.number}>
              <div className="bestellkarte-kopf">
                <span className="bestellkarte-nummer">{bestellung.number}</span>
                <Standmarke status={bestellung.status} />
              </div>

              <p className="bestellkarte-zeile">
                {/* Datum, Stückzahl und Betrag in einer Zeile: Das sind die
                    drei Merkmale, an denen jemand seine Bestellung wiedererkennt. */}
                {datum(bestellung.createdAt) && <span>{datum(bestellung.createdAt)}</span>}
                <span>{tz(stueck, 'meine.artikelEiner', 'meine.artikelViele')}</span>
                <strong>{preis(bestellung.totalGross)}</strong>
              </p>

              {/* Ein Verweis auf die Bestellseite und kein Aufklappen.
                  
                  Vorher gab es dieselbe Bestellung in zwei Formen an zwei
                  Stellen: als ganze Seite unter /bestellung/:nummer und
                  aufgeklappt in dieser Karte. Zwei Ansichten derselben Sache
                  driften auseinander — was hier fehlte, fiel niemandem auf,
                  weil es dort ja stand. Jetzt führt beides an denselben Ort.
                  
                  Ohne Schlüssel in der Adresse: Wer angemeldet ist, weist sich
                  über sein Konto aus, und ein Schlüssel im Verlauf des
                  Browsers wäre einer zu viel. */}
              <Link className="verweisknopf" to={`/bestellung/${encodeURIComponent(bestellung.number)}`}>
                {t('meine.ansehen')}
              </Link>
            </li>
          )
        })}
      </ul>
    </>
  )
}
